import { Helmet } from "react-helmet-async";

// Central SEO configuration for LiberoX Labs.
export const SITE = {
  name: "LiberoX Labs",
  legalName: "Liberox GmbH",
  baseUrl: "https://labs.liberox.ch",
  locale: "de_CH",
  lang: "de-CH",
  defaultTitle: "LiberoX Labs – KI-Agenten, App- & Web-Entwicklung aus der Schweiz",
  titleTemplate: "%s | LiberoX Labs",
  defaultDescription:
    "LiberoX Labs entwickelt autonome KI-Agenten, Mobile-Apps, Web-Apps und massgeschneiderte Plattformen. Unsere Spezialität: KI-Agenten & Automatisierung, von Strategie über Design bis Engineering. 100% Geld-zurück-Garantie.",
  keywords: [
    "KI-Agenten erstellen",
    "KI Agenten Entwicklung",
    "AI Agents Schweiz",
    "KI Automatisierung",
    "App-Entwicklung Schweiz",
    "Web-App Entwicklung",
    "Softwareentwicklung Schweiz",
    "CRM Entwicklung",
    "Individualsoftware",
    "React FastAPI",
    "LiberoX Labs",
  ],
  ogImage: "/liberox-genesis-icon.png",
  twitter: "@liberox",
};

export const abs = (path = "/") =>
  path.startsWith("http") ? path : `${SITE.baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  legalName: SITE.legalName,
  url: SITE.baseUrl,
  logo: abs("/liberox-genesis-icon.png"),
  email: "labs@liberox.ch",
  areaServed: "CH",
  sameAs: [],
  description: SITE.defaultDescription,
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE.baseUrl,
  inLanguage: SITE.lang,
});

export const serviceJsonLd = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType: name,
  areaServed: "CH",
  url: abs(path),
  provider: {
    "@type": "Organization",
    name: SITE.name,
    url: SITE.baseUrl,
  },
});

export const faqJsonLd = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbJsonLd = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: abs(it.path),
  })),
});

export const Seo = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  const metaTitle = title || SITE.defaultTitle;
  const fullTitle = title ? `${title} | ${SITE.name}` : SITE.defaultTitle;
  const metaDescription = description || SITE.defaultDescription;
  const url = abs(path);
  const img = abs(image || SITE.ogImage);
  const structured = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang={SITE.lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={SITE.keywords.join(", ")} />
      <meta name="author" content={SITE.name} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={img} />

      {structured.map((data, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};
